import { useMemo, useCallback, useState, useEffect } from 'react';
import { useFormStore } from '../../store/formStore';
import { loadConfig } from '../../engine/configLoader';
import { computeVisibility } from '../../engine/visibilityProcessor';
import { AttributeEngine } from '../attribute/AttributeEngine';

export function useProductEngine() {
  const store = useFormStore();
  const { solutionId, role, values, errors, isDirty, isSubmitting, lastSaved } = store;
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    loadConfig(solutionId, role)
      .then(res => { if (active) { setConfig(res); setIsLoading(false); } })
      .catch(err => { console.error('[ProductEngine] Config load error:', err); if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [solutionId, role]);

  const visibility = useMemo(() => {
    if (!config) return null;
    return computeVisibility(config.solution, config.attributes, config.groups, config.rolePermission, role, values);
  }, [config, values, role]);

  const handleFieldChange = useCallback((fieldId, value) => {
    const attr = config?.attributes.get(fieldId);
    store.setFieldValue(fieldId, value, attr?.scope);
  }, [store, config]);

  const handleSubmit = useCallback(async (onSuccess) => {
    if (!config || !visibility) return;
    store.setSubmitting(true);
    store.clearErrors();

    const errs = AttributeEngine.validateForm(
      config.attributes,
      Array.from(visibility.visibleAttributeIds),
      values,
      visibility.computedState.requiredOverrides,
      visibility.computedState.unrequiredOverrides,
    );

    if (Object.keys(errs).length > 0) {
      store.setErrors(errs);
      store.setSubmitting(false);
      const firstId = Object.keys(errs)[0];
      document.getElementById(`field-${firstId}`)?.scrollIntoView({ behavior:'smooth', block:'center' });
      return;
    }

    try {
      const payload = Object.fromEntries(
        Array.from(visibility.visibleAttributeIds)
          .map(id => [id, values[id]])
          .filter(([, v]) => v !== undefined)
      );
      await onSuccess(payload);
      store.resetForm();
    } catch (err) {
      console.error('[ProductEngine] Submit error:', err);
    } finally {
      store.setSubmitting(false);
    }
  }, [config, visibility, values, store]);

  const handleSaveDraft = useCallback(() => store.saveDraft(), [store]);

  return {
    config, visibility,
    layout: config?.layout ?? null,
    solutionId, role, values, errors, isDirty, isSubmitting, lastSaved,
    handleFieldChange, handleSubmit, handleSaveDraft, isLoading,
    setSolution: store.setSolution,
    setRole: store.setRole,
  };
}
